import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { searchUsers, type UserSearchItem } from "../lib/userSearchApi";
import Avatar from "./Avatar";
import FollowButton from "./FollowButton";
import { API_BASE_URL } from "../lib/api";

function abs(url?: string | null) {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

type SavWarTopic = {
  _id: string;
  tag: string;
  slug: string;
  totalSessions: number;
  lastUsedAt: string;
};

export default function InlineUserSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<UserSearchItem[]>([]);
  const [savwarTopics, setSavwarTopics] = useState<SavWarTopic[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(-1);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const navigate = useNavigate();

  const debounced = useDebounced(q, 300);
  const isHashtagSearch = debounced.trim().startsWith('#');
  const showList = open && debounced.trim().length > 0 && (isHashtagSearch || loading || items.length > 0 || savwarTopics.length > 0);

  // Pozisyon hesapla
  useEffect(() => {
    if (showList && boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showList]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const s = debounced.trim();
      if (!s) {
        setItems([]);
        setSavwarTopics([]);
        setLoading(false);
        return;
      }
      
      // Hashtag araması için SavWar topic search kullan
      if (s.startsWith('#')) {
        setItems([]);
        setLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/api/savwar/topics/search?q=${encodeURIComponent(s)}`);
          const data = await response.json();
          if (!dead && data.success) {
            setSavwarTopics(data.data?.topics || []);
          }
        } catch (error) {
          console.error('SavWar topic search error:', error);
          if (!dead) setSavwarTopics([]);
        } finally {
          if (!dead) setLoading(false);
        }
        return;
      }
      
      setLoading(true);
      setSavwarTopics([]);
      try {
        // GLOBAL arama: takip sınırlaması yok
        const res = await searchUsers(s, 8);
        if (!dead) setItems(res);
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [debounced]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function submitFull() {
    const s = q.trim();
    if (!s) return;
    setOpen(false);
    
    // Hashtag araması için - eğer sonuç varsa ilkini seç, yoksa arama sayfasına git
    if (s.startsWith('#')) {
      if (savwarTopics.length > 0) {
        navigate(`/savwar/topics/${savwarTopics[0].slug}`);
      } else {
        navigate(`/search/users?q=${encodeURIComponent(s)}`);
      }
      return;
    }
    
    navigate(`/search/users?q=${encodeURIComponent(s)}`);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showList) {
      if (e.key === "Enter") submitFull();
      return;
    }
    
    const maxIdx = isHashtagSearch ? savwarTopics.length - 1 : items.length - 1;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((v) => Math.min(maxIdx, v + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((v) => Math.max(-1, v - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isHashtagSearch) {
        if (idx >= 0 && savwarTopics[idx]) {
          navigate(`/savwar/topics/${savwarTopics[idx].slug}`);
          setOpen(false);
        } else {
          submitFull();
        }
      } else {
        if (idx >= 0 && items[idx]) {
          navigate(`/u/${encodeURIComponent(items[idx].username)}`);
          setOpen(false);
        } else {
          submitFull();
        }
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value.slice(0, 40));
          setIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Kullanıcı veya #hashtag ara…"
        aria-label="Kullanıcı veya hashtag ara"
        style={{
          width: "100%",
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px solid #222",
          background: "#0f0f0f",
          color: "inherit",
        }}
      />

      {showList && dropdownPosition && createPortal(
        <div
          role="listbox"
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            background: "#0b0b0b",
            border: "1px solid #222",
            borderRadius: 10,
            padding: 6,
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            maxHeight: 360,
            overflow: "auto",
            zIndex: 999999,
          }}
        >
          {isHashtagSearch ? (
            <>
              {loading && <div style={{ padding: 8, opacity: 0.8 }}>Aranıyor…</div>}
              {!loading && savwarTopics.length === 0 && (
                <div style={{ padding: 8, opacity: 0.7 }}>Sonuç bulunamadı</div>
              )}
              {!loading && savwarTopics.map((topic, i) => (
                <div
                  key={topic._id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    navigate(`/savwar/topics/${topic.slug}`);
                    setOpen(false);
                  }}
                  onMouseEnter={() => setIdx(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderRadius: 8,
                    background: i === idx ? "#1a1a1a" : "#121212",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  <div style={{ fontSize: 24 }}>⚔️</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: "#FFD700" }}>
                      {topic.tag}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>
                      {topic.totalSessions} oturum
                    </div>
                  </div>
                </div>
              ))}
              {!loading && savwarTopics.length > 0 && (
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    navigate(`/search/users?q=${encodeURIComponent(q.trim())}`);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #2c2c2c",
                    background: "transparent",
                  }}
                >
                  Tüm sonuçları gör
                </button>
              )}
            </>
          ) : (
            <>
              {loading && <div style={{ padding: 8, opacity: 0.8 }}>Aranıyor…</div>}
              {!loading && items.length === 0 && (
                <div style={{ padding: 8, opacity: 0.7 }}>Sonuç yok</div>
              )}

              {!loading &&
                items.map((u, i) => (
                  <div
                    key={u.id}
                    role="option"
                    aria-selected={i === idx}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      navigate(`/u/${encodeURIComponent(u.username)}`);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setIdx(i)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "28px 1fr auto",
                      gap: 8,
                      alignItems: "center",
                      padding: 6,
                      borderRadius: 8,
                      background: i === idx ? "#121212" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Avatar src={abs(u.avatarUrlSmall || u.avatarUrl)} alt={u.username} size={28} />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        @{u.username}
                      </div>
                      {u.bio && (
                        <div
                          style={{
                            opacity: 0.75,
                            fontSize: 12,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {u.bio}
                        </div>
                      )}
                    </div>
                    <FollowButton userId={u.id} username={u.username} size="sm" />
                  </div>
                ))}

              {!loading && items.length > 0 && (
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    submitFull();
                  }}
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #2c2c2c",
                    background: "transparent",
                  }}
                >
                  Tüm sonuçları gör
                </button>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
